
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Camera, Upload, ArrowLeft } from "lucide-react";
import Header from "@/components/Header";
import ImageCapture from "@/components/ImageCapture";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useSubscription } from "@/hooks/subscription/useSubscription";

export default function CameraPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isOnPaidPlan, userCredits, refreshData } = useSubscription();
  const [isUploading, setIsUploading] = useState(false);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);

  const fetchUserData = async () => {
    if (!user) return;

    try {
      // Refresh subscription data to get latest credits information
      await refreshData();
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  const handleImageCaptured = (imageDataUrl: string) => {
    setCapturedImage(imageDataUrl);
    localStorage.setItem("capturedImage", imageDataUrl);
    // Clear any previous processing flag - this is a fresh image
    localStorage.removeItem("imageProcessed");
    toast.success("Image captured successfully");
    console.log("Image captured and stored:", !!imageDataUrl);

    if (user) {
      fetchUserData();
    }
  };

  const handleGenerateIdeas = async () => {
    console.log("handleGenerateIdeas start");
    if (!capturedImage) {
      toast.error(
        "No image available. Please capture or upload an image first."
      );
      return;
    }

    // Check if this image was already processed
    const imageProcessed = localStorage.getItem("imageProcessed");
    if (imageProcessed === "true") {
      toast.error("This image has already been processed. Please capture a new image.");
      return;
    }

    if (!user) {
      toast.error("You need to be logged in to generate ideas with AI.");
      navigate("/auth");
      return;
    }

    await fetchUserData();

    // Check if user has sufficient credits
    if (!userCredits || userCredits.credits_balance < 1) {
      toast.error(
        `You don't have enough credits to generate ideas. ${
          isOnPaidPlan()
            ? "Your credits will reset next month according to your subscription."
            : "Please upgrade to a paid plan for more credits."
        }`
      );
      return;
    }

    try {
      setIsUploading(true);
      toast.info("Processing image with AI...");

      const resultId = "result_" + Date.now();

      // Navigate with generation flag to indicate explicit idea generation
      navigate(`/results/${resultId}?generate=true`);
    } catch (error) {
      console.error("Error processing image:", error);
      toast.error("Failed to process image. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchUserData();
    }
  }, [user]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/30">
      <Header />

      <main className="container max-w-4xl mx-auto pt-16 pb-32 px-4">
        <div className="text-center mb-4">
          <Button
            variant="ghost"
            size="sm"
            className="absolute left-4 top-16 sm:left-8"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>

          <h1 className="text-3xl font-bold font-display mt-6">
            Capture Objects
          </h1>
          <p className="text-muted-foreground mt-2 max-w-md mx-auto">
            Take a photo of objects you'd like to transform into creative
            activities
          </p>
        </div>

        <div className="max-w-md mx-auto">
          <div className="relative glass-card rounded-2xl overflow-hidden p-4 animate-fade-in-up">
            {isUploading && (
              <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm z-10">
                <div className="text-center">
                  <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-primary font-medium">
                    Processing image with AI...
                  </p>
                </div>
              </div>
            )}

            <ImageCapture 
              onImageCaptured={handleImageCaptured}
              capturedImage={capturedImage}
              onGenerateIdeas={handleGenerateIdeas}
              isGenerating={isUploading}
              userCredits={userCredits}
            />
          </div>
        </div>

        {/* Tips section moved to bottom and made more compact */}
        <div className="mt-6 text-center space-y-3 max-w-md mx-auto">
          <p className="text-sm text-muted-foreground">
            For best results, make sure your objects are clearly visible
            and well-lit
          </p>

          <div className="flex flex-wrap justify-center gap-2">
            <div className="flex items-center bg-white/60 px-3 py-1.5 rounded-full text-xs font-medium text-muted-foreground">
              <Camera className="h-3 w-3 mr-1.5 text-primary" />
              Clear photos work best
            </div>

            <div className="flex items-center bg-white/60 px-3 py-1.5 rounded-full text-xs font-medium text-muted-foreground">
              <Upload className="h-3 w-3 mr-1.5 text-primary" />
              Or upload existing images
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
